import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  getScrollTargetForCurrentUrl,
  markScrollRestored,
} from "@/lib/authReturn";

/**
 * After OAuth/email auth returns the user to a saved URL, smoothly restore
 * their previous scroll position once the page content is tall enough.
 * Falls back gracefully if content never reaches the saved offset.
 */
export default function AuthReturnRestorer() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    const target = getScrollTargetForCurrentUrl();
    if (target == null || target <= 0) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 40; // ~4s at 100ms

    // Disable browser's automatic scroll restoration so we control it.
    const prev = window.history.scrollRestoration;
    try {
      window.history.scrollRestoration = "manual";
    } catch {
      /* ignore */
    }

    const tryRestore = () => {
      if (cancelled) return;
      attempts += 1;
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll >= target - 4 || attempts >= maxAttempts) {
        window.scrollTo({ top: target, behavior: "smooth" });
        markScrollRestored();
        try {
          if (prev) window.history.scrollRestoration = prev;
        } catch {
          /* ignore */
        }
        return;
      }
      setTimeout(tryRestore, 100);
    };

    // Defer to next frame so layout settles.
    const raf = requestAnimationFrame(() => tryRestore());

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search, hash]);

  return null;
}
